import api from "./api";

export interface NFCCard {
  id: string;
  /** Physical UID — only sent when registering; never returned by API */
  cardNumber?: string;
  displayNumber: string;
  balance: number;
  holderId: string | null;
  branchId: string;
  status: "ACTIVE" | "INACTIVE" | "BLOCKED" | "LOST";
  blockedReason?: string | null;
  blockedAt?: string | null;
  isAssigned: boolean;
  assignedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  holder?: {
    id: string;
    holderName: string;
    phone: string;
    email?: string;
  };
}

export interface CreateNFCCardData {
  cardNumber: string;
  branchId: string;
  balance?: number;
  notes?: string;
}

export interface NFCCardFilter {
  branchId?: string;
  isAssigned?: boolean;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export const nfcCardAPI = {
  getCards: async (filters?: NFCCardFilter) => {
    const response = await api.get("/nfc-cards", { params: filters });
    const payload = response.data.data;
    return {
      cards: payload.cards || [],
      total: payload.total || 0,
      page: payload.page || 1,
      totalPages: payload.totalPages || 1,
    };
  },

  getCardById: async (id: string): Promise<NFCCard> => {
    const response = await api.get(`/nfc-cards/${id}`);
    return response.data.data;
  },

  getCardByNumber: async (cardNumber: string): Promise<NFCCard> => {
    const response = await api.get(`/nfc-cards/by-number/${cardNumber}`);
    return response.data.data;
  },

  getCardsByHolder: async (holderId: string) => {
    const response = await api.get(`/nfc-cards/holder/${holderId}`);
    return response.data.data;
  },

  createCard: async (data: CreateNFCCardData): Promise<NFCCard> => {
    const response = await api.post("/nfc-cards", data);
    return response.data.data;
  },

  updateCard: async (
    id: string,
    data: Partial<Pick<NFCCard, "balance" | "status" | "notes" | "blockedReason">>
  ): Promise<NFCCard> => {
    const response = await api.put(`/nfc-cards/${id}`, data);
    return response.data.data;
  },

  assignCard: async (id: string, holderId: string): Promise<NFCCard> => {
    const response = await api.post(`/nfc-cards/${id}/assign`, { holderId });
    return response.data.data;
  },

  unassignCard: async (id: string): Promise<NFCCard> => {
    const response = await api.post(`/nfc-cards/${id}/unassign`);
    return response.data.data;
  },

  deleteCard: async (id: string) => {
    const response = await api.delete(`/nfc-cards/${id}`);
    return response.data;
  },

  /**
   * NFC Cards page: register physical card in inventory only (no holder, zero balance).
   */
  registerInventoryCard: async (
    cardNumber: string,
    branchId: string
  ): Promise<NFCCard> => {
    const num = cardNumber.trim();
    if (!num) throw new Error("Card number required");
    if (!branchId) throw new Error("Branch is required to register a card");

    return nfcCardAPI.createCard({
      cardNumber: num,
      branchId,
      balance: 0,
    });
  },

  /**
   * Holder profile: link an existing unassigned card from nfc_cards to this holder.
   */
  linkCardToHolder: async (
    cardNumber: string,
    holderId: string
  ): Promise<NFCCard> => {
    const num = cardNumber.trim();
    if (!num) throw new Error("Card number required");

    let existing: NFCCard;
    try {
      existing = await nfcCardAPI.getCardByNumber(num);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "";
      if (err?.response?.status === 404 || /not found/i.test(String(msg))) {
        throw new Error(
          "Card is not in the system. Register it first from NFC Cards, then link it here."
        );
      }
      throw err;
    }

    if (existing.holderId && existing.holderId !== holderId) {
      const who = existing.holder?.holderName || "another holder";
      throw new Error(`Card is already assigned to ${who}`);
    }

    if (existing.holderId === holderId) {
      throw new Error("This card is already on this holder");
    }

    return nfcCardAPI.assignCard(existing.id, holderId);
  },
};
